import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { ListSkeleton } from "@/components/PageSkeleton";
import { Markdown } from "@/components/TwinChat";
import {
  listDeals,
  proposeDeal,
  respondDeal,
  signDeal,
  setDealStatus,
} from "@/lib/deals.functions";
import {
  Handshake,
  Send,
  Check,
  X,
  PenLine,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { SignatureBlock } from "@/components/contracts/SignatureBlock";
import { useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/deals")({
  head: () =>
    buildSeo({
      path: "/deals",
      title: "Deals — Reality Twin",
      description:
        "Turn twin-to-twin meetings into signed deals: drafted term sheets, dual e-signature and status tracking from proposal to close.",
      noindex: true,
    }),
  component: Page,
});

const STATUS_HUE: Record<string, string> = {
  draft: "60",
  proposed: "240",
  accepted: "200",
  declined: "20",
  signed: "168",
  paid: "140",
  completed: "290",
};

/** Stages in the order a deal moves through them. Terminal states sit apart:
 *  mixing "declined" into a pipeline total is how forecasts start lying. */
const OPEN_STAGES = ["draft", "proposed", "accepted", "signed", "paid"] as const;

/**
 * What is actually in the pipeline, by stage and by value.
 *
 * A flat list answers "what exists". An owner needs to know where value is
 * stuck and what is waiting on them — so open value, won value and the count
 * per stage lead the page.
 */
function PipelineSummary({ deals }: { deals: any[] }) {
  const t = useHydratedTranslation();

  const byStage = new Map<string, { count: number; value: number }>();
  let openValue = 0;
  let wonValue = 0;
  const currencies = new Set<string>();

  for (const d of deals) {
    const amount = Number(d.amount) || 0;
    if (d.currency) currencies.add(d.currency);
    const bucket = byStage.get(d.status) ?? { count: 0, value: 0 };
    bucket.count += 1;
    bucket.value += amount;
    byStage.set(d.status, bucket);
    if (d.status === "completed") wonValue += amount;
    else if (OPEN_STAGES.includes(d.status)) openValue += amount;
  }

  // Summing across currencies would produce a meaningless number, so say so
  // rather than printing a total that is quietly wrong.
  const mixed = currencies.size > 1;
  const currency = currencies.size === 1 ? [...currencies][0] : "";
  const money = (n: number) => (mixed ? "—" : `${n.toLocaleString()} ${currency}`.trim());

  return (
    <div className="glass-strong rounded-2xl p-5">
      <div className="flex flex-wrap gap-6">
        <div>
          <div className="font-display text-2xl">{money(openValue)}</div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-0.5">
            {t("app.pages.deals.openValue", { defaultValue: "Open pipeline" })}
          </div>
        </div>
        <div>
          <div className="font-display text-2xl text-[oklch(0.82_0.18_168)]">{money(wonValue)}</div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-0.5">
            {t("app.pages.deals.wonValue", { defaultValue: "Completed" })}
          </div>
        </div>
      </div>

      {mixed && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t("app.pages.deals.mixedCurrency", {
            defaultValue:
              "Deals span several currencies, so no total is shown — a summed figure would be wrong.",
          })}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-1.5">
        {OPEN_STAGES.map((s) => {
          const b = byStage.get(s);
          if (!b) return null;
          return (
            <div
              key={s}
              className="rounded-xl border border-border bg-white/[0.02] px-3 py-2 min-w-[104px]"
            >
              <div className="font-display text-lg leading-none">{b.count}</div>
              <div className="text-[10.5px] text-muted-foreground mt-1">
                {t(`app.pages.deals.st_${s}`, { defaultValue: s })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Page() {
  const t = useHydratedTranslation();
  const fetchDeals = useServerFn(listDeals);
  const propose = useServerFn(proposeDeal);
  const respond = useServerFn(respondDeal);
  const sign = useServerFn(signDeal);
  const setStatus = useServerFn(setDealStatus);

  const [data, setData] = useState<{ deals: any[]; userId: string } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [signingFor, setSigningFor] = useState<string | null>(null);

  async function load() {
    try {
      setData((await fetchDeals()) as any);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function run(id: string, fn: () => Promise<any>, ok?: string) {
    setBusy(id);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setBusy(null);
    }
  }

  const deals = data?.deals ?? [];
  const uid = data?.userId;

  return (
    <DashboardShell title={t("app.pages.deals.title")} subtitle={t("app.pages.deals.subtitle")}>
      {!data ? (
        <ListSkeleton rows={3} />
      ) : deals.length === 0 ? (
        <div className="glass-strong rounded-2xl p-12 text-center">
          <Handshake className="h-8 w-8 mx-auto text-[oklch(0.82_0.18_168)] mb-3" />
          <div className="font-display text-lg font-semibold">
            {t("app.pages.deals.emptyTitle")}
          </div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            {t("app.pages.deals.emptyBody")}
          </p>
          <Link
            to="/opportunities"
            className="mt-5 inline-flex rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium"
          >
            {t("app.pages.deals.toMeetings")}
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          <PipelineSummary deals={deals} />
          {deals.map((d) => {
            const isA = uid === d.owner_a;
            const counterpart = isA ? d.company_b_name : d.company_a_name;
            const mySigned = isA ? d.signed_a : d.signed_b;
            const isOpen = open === d.id;
            return (
              <div key={d.id} className="glass-strong rounded-2xl p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-md glass"
                        style={{ color: `oklch(0.82 0.15 ${STATUS_HUE[d.status] ?? "168"})` }}
                      >
                        {t(`app.pages.deals.st_${d.status}`, { defaultValue: d.status })}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t("app.pages.deals.with", { name: counterpart })}
                      </span>
                    </div>
                    <div className="mt-2 font-display text-lg">{d.title}</div>
                    {d.amount != null && (
                      <div className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
                        <CircleDollarSign className="h-3 w-3" />
                        {Number(d.amount).toLocaleString()} {d.currency}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className={d.signed_a ? "text-[oklch(0.82_0.18_168)]" : ""}>
                        {d.signed_a ? "✓ " : "○ "}
                        {d.company_a_name}
                      </span>
                      <span className={d.signed_b ? "text-[oklch(0.82_0.18_168)]" : ""}>
                        {d.signed_b ? "✓ " : "○ "}
                        {d.company_b_name}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0 items-center">
                    {d.status === "draft" && isA && (
                      <button
                        disabled={busy === d.id}
                        onClick={() =>
                          run(
                            d.id,
                            () => propose({ data: { id: d.id } }),
                            t("app.pages.deals.sent"),
                          )
                        }
                        className="rounded-full brand-gradient text-black px-4 py-1.5 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        <Send className="h-3 w-3" /> {t("app.pages.deals.send")}
                      </button>
                    )}
                    {d.status === "proposed" && !isA && (
                      <>
                        <button
                          disabled={busy === d.id}
                          onClick={() =>
                            run(
                              d.id,
                              () => respond({ data: { id: d.id, accept: true } }),
                              t("app.pages.deals.accepted"),
                            )
                          }
                          className="rounded-full brand-gradient text-black px-4 py-1.5 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          <Check className="h-3 w-3" /> {t("app.pages.deals.accept")}
                        </button>
                        <button
                          disabled={busy === d.id}
                          onClick={() =>
                            run(d.id, () => respond({ data: { id: d.id, accept: false } }))
                          }
                          className="rounded-full glass border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </>
                    )}
                    {d.status === "proposed" && isA && (
                      <span className="text-xs text-muted-foreground">
                        {t("app.pages.deals.awaiting")}
                      </span>
                    )}
                    {(d.status === "accepted" || d.status === "signed") && !mySigned && (
                      <button
                        disabled={busy === d.id}
                        onClick={() => {
                          setSigningFor(signingFor === d.id ? null : d.id);
                          setOpen(d.id);
                        }}
                        className="rounded-full brand-gradient text-black px-4 py-1.5 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        <PenLine className="h-3 w-3" /> {t("app.pages.deals.sign")}
                      </button>
                    )}
                    {d.status === "signed" && isA && (
                      <button
                        disabled={busy === d.id}
                        onClick={() =>
                          run(
                            d.id,
                            () => setStatus({ data: { id: d.id, status: "paid" } }),
                            t("app.pages.deals.paidToast"),
                          )
                        }
                        className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
                      >
                        {t("app.pages.deals.markPaid")}
                      </button>
                    )}
                    {(d.status === "paid" || d.status === "signed") && isA && (
                      <button
                        disabled={busy === d.id}
                        onClick={() =>
                          run(
                            d.id,
                            () => setStatus({ data: { id: d.id, status: "completed" } }),
                            t("app.pages.deals.completedToast"),
                          )
                        }
                        className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
                      >
                        {t("app.pages.deals.markCompleted")}
                      </button>
                    )}
                    <button
                      onClick={() => setOpen(isOpen ? null : d.id)}
                      className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5 inline-flex items-center gap-1"
                    >
                      {isOpen ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                      {t("app.pages.deals.terms")}
                    </button>
                  </div>
                </div>
                {isOpen && d.terms_md && (
                  <div className="mt-4 border-t border-border pt-4 text-sm text-muted-foreground">
                    <Markdown text={d.terms_md} sources={[]} />
                    {d.terms_hash && (
                      <div className="mt-3 text-[10.5px] font-mono text-muted-foreground/80 break-all">
                        SHA-256 {String(d.terms_hash).slice(0, 16)}…
                        <span className="ml-1 font-sans">
                          {t("app.pages.deals.frozenNote", {
                            defaultValue:
                              "— terms locked at first signature; both signatures bind to this fingerprint",
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Signing is a deliberate act with a name behind it, not a
                    button that flips a flag. */}
                {signingFor === d.id && !mySigned && (
                  <div className="mt-4">
                    <SignatureBlock
                      busy={busy === d.id}
                      label={t("app.pages.deals.sign")}
                      onSign={(name) =>
                        run(
                          d.id,
                          async () => {
                            await sign({
                              data: {
                                id: d.id,
                                signerName: name,
                                accepted: true,
                                termsHash: d.terms_hash ?? null,
                              },
                            });
                            setSigningFor(null);
                          },
                          t("app.pages.deals.signedToast"),
                        )
                      }
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DashboardShell>
  );
}
